import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, NavLink, Link, useLocation } from 'react-router-dom';
import POSTS from './posts';
import FoodPage from './Food';
import FoodLog from './FoodLog';
import FoodLogMulti from './FoodLogMulti';
import Cockpit from './Cockpit';
import ResumePage from './Resume';
import PhotosPage from './Photos';
import MapUpload from './MapUpload';
import { prefetchMapPhotos } from './mapPhotos';
import './style.css';

// ---------- helpers ----------
function fmtDate(iso, opts = {}) {
  const d = new Date(iso + 'T00:00:00');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  if (opts.long) {
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  }
  return `${String(d.getDate()).padStart(2,'0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

// ---------- tweet embed ----------
function TweetEmbed({ id }) {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const render = () => {
      container.innerHTML = '';
      const theme = document.documentElement.dataset.theme || 'light';
      window.twttr.widgets
        .createTweet(id, container, { theme, dnt: true, align: 'center' })
        .then(el => { if (!el) setFailed(true); })
        .catch(() => setFailed(true));
    };

    window.twttr = window.twttr || { _e: [], ready(f) { this._e.push(f); } };
    window.twttr.ready(render);

    if (!document.getElementById('twitter-wjs')) {
      const s = document.createElement('script');
      s.id = 'twitter-wjs';
      s.src = 'https://platform.twitter.com/widgets.js';
      s.async = true;
      s.onerror = () => setFailed(true);
      document.head.appendChild(s);
    }
  }, [id]);

  if (failed) {
    return (
      <div className="tweet-fallback">
        <a href={`https://twitter.com/i/web/status/${id}`} target="_blank" rel="noopener noreferrer">
          View tweet on X ↗
        </a>
      </div>
    );
  }

  return <div ref={ref} className="tweet-embed" />;
}

function renderInline(text, key) {
  const parts = [];
  let last = 0;
  const re = /\*([^*]+)\*/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<em key={key + '-' + m.index}>{m[1]}</em>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function stripFrontmatter(text) {
  const t = text.replace(/\r\n/g, '\n').trimStart();
  if (!t.startsWith('---\n')) return text;
  const end = t.indexOf('\n---\n', 4);
  if (end === -1) return text;
  return t.slice(end + 5).trimStart();
}

function renderBody(text) {
  const blocks = stripFrontmatter(text).trim().split(/\n\n+/);
  return blocks.map((block, i) => {
    const b = block.trim();
    if (b.startsWith('## ')) {
      return <h2 key={i} className="reader-h2">{b.slice(3)}</h2>;
    }
    const tweetMatch = b.match(/^\[tweet:(\d+)\]$/);
    if (tweetMatch) {
      return <TweetEmbed key={i} id={tweetMatch[1]} />;
    }
    if (b.startsWith('> ')) {
      return <blockquote key={i} className="reader-blockquote">{renderInline(b.slice(2), i)}</blockquote>;
    }
    const imgMatch = b.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgMatch) {
      return <img key={i} className="reader-img" src={imgMatch[2]} alt={imgMatch[1]} />;
    }
    return <p key={i}>{renderInline(b, i)}</p>;
  });
}

// ---------- header ----------
const SECTIONS = [
  { key: 'writing', label: 'Writing', href: '/' },
  { key: 'photos', label: 'Map', href: '/photos' },
  { key: 'lunch', label: 'Lunch', href: '/lunch' },
  { key: 'resume', label: 'Résumé', href: '/resume' },
];

function SiteNav() {
  return (
    <nav className="site-nav" aria-label="Sections">
      {SECTIONS.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 ? <span className="site-nav-sep" aria-hidden="true">·</span> : null}
          <NavLink
            to={s.href}
            end={s.href === '/'}
            className={({ isActive }) => 'site-nav-link' + (isActive ? ' active' : '')}
          >
            {s.label}
          </NavLink>
        </React.Fragment>
      ))}
    </nav>
  );
}

function Header({ theme, onToggleTheme }) {
  return (
    <header className="site-header">
      <Link className="wordmark" to="/" aria-label="Home">
        sandeep<span className="wordmark-dot">.</span>
      </Link>
      <div className="site-nav-wrap">
        <SiteNav />
      </div>
      <button
        className="theme-toggle"
        onClick={onToggleTheme}
        aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
      >
        {theme === 'dark' ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round">
            <circle cx="8" cy="8" r="3" />
            <path d="M8 1.5v1.5M8 13v1.5M1.5 8H3M13 8h1.5M3.3 3.3l1 1M11.7 11.7l1 1M3.3 12.7l1-1M11.7 4.3l1-1" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
            <path d="M13.5 9.5A6 6 0 1 1 6.5 2.5a5 5 0 0 0 7 7z" />
          </svg>
        )}
      </button>
    </header>
  );
}

// ---------- bio ----------
const EXPERIENCE = [
  {
    company: 'Fin',
    role: 'Product Engineer',
    period: 'Mar 2026 – Present',
    location: 'London, UK',
    highlights: ['Just getting started — more to come.'],
  },
  {
    company: 'Bolt',
    role: 'Senior Software Engineer',
    period: 'Jul 2025 – Mar 2026',
    location: 'Tallinn, Estonia',
    highlights: [
      'Spearheaded Cashback for Bolt Food across 7+ cross-functional teams',
      'Projected €1M+ annual savings by replacing standard discounts with expiring cashbacks',
    ],
  },
  {
    company: 'Bolt',
    role: 'Software Engineer',
    period: 'Nov 2022 – Jul 2025',
    location: 'Tallinn, Estonia',
    highlights: [
      'Led Bolt Plus subscription model, scaled to 10+ countries — €220K revenue in Q1 2025',
      'Architected Restaurant Portal partner discounts — €3M revenue in 6 months',
    ],
  },
  {
    company: 'Disney+ Hotstar',
    role: 'Platform Engineer',
    period: 'Aug 2021 – Oct 2022',
    location: 'Bengaluru, India',
    highlights: [
      'Cut Kubernetes deployment time by 40% with internal automation tooling',
      'Maintained 99.95% availability with Grafana + Victoria Metrics',
    ],
  },
  {
    company: 'Thoughtworks',
    role: 'Application Developer',
    period: 'Jun 2019 – Aug 2021',
    location: 'Bengaluru, India',
    highlights: [
      'Doubled e-commerce client revenue by building scalable microservices',
      'Optimised JPA queries to handle 3000+ requests/second',
    ],
  },
];

function Bio() {
  const [open, setOpen] = useState(false);
  return (
    <section className="bio">
      <h1 className="name">Sandeep Jaiswal</h1>
      <p className="role">
        <span className="role-title">Product Engineer</span>
        <span className="role-sep"> at </span>
        <span className="role-company">Fin</span>
        <span className="role-dot"> · </span>
        <span className="role-loc">London</span>
      </p>

      <div className="bio-body">
        <p>
          I build software. Seven years, a few companies, a lot of countries — mostly at scale, occasionally on time.
        </p>
        <p className="now-line">
          <span className="now-dot" aria-hidden="true"></span>
          Just moved to London after three and a half years at Bolt in
          Tallinn. Currently settling into the city and helping Fin's
          customers test and deploy Fin.
        </p>

        <button
          className="more-toggle"
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
        >
          {open ? 'Work history ↑' : 'Work history ↓'}
        </button>

        <div className={'more-panel ' + (open ? 'open' : '')}>
          <div className="more-inner">
            <div className="more-row">
              <div className="more-label">Work</div>
              <div className="more-value">
                <ol className="cv-list">
                  {EXPERIENCE.map((job, i) => (
                    <li key={i} className="cv-row">
                      <div className="cv-period">{job.period}</div>
                      <div className="cv-body">
                        <div className="cv-head">
                          <span className="cv-jrole">{job.role}</span>
                          <span className="cv-sep">·</span>
                          <span className="cv-company">{job.company}</span>
                          <span className="cv-loc">{job.location}</span>
                        </div>
                        <ul className="cv-highlights">
                          {job.highlights.map((h, j) => (
                            <li key={j}>{h}</li>
                          ))}
                        </ul>
                      </div>
                    </li>
                  ))}
                </ol>
                <Link className="cv-resume" to="/resume">Full résumé ↗</Link>
              </div>
            </div>

            <div className="more-row">
              <div className="more-label">Stack</div>
              <div className="more-value">
                Most at home in TypeScript, React, Node, and SQL. Have happily
                shipped Ruby on Rails, Java/Spring, Kubernetes, and a fair
                amount of glue in between. Happiest near the product surface —
                where the thing meets the person using it.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- projects ----------
const PROJECTS = [
  {
    name: 'Bus Tracker',
    year: '2025',
    blurb: 'A personal bus tracker that watches the Tallinn routes I rode every day and the occupancy of my gym — built to make my commute and workout planning slightly less annoying.',
    stack: ['React', 'TypeScript', 'Node.js'],
    url: 'https://bus-tracker-wheat.vercel.app/',
  },
];

function Projects() {
  return (
    <section className="projects">
      <div className="section-head">
        <h2 className="section-heading">Projects</h2>
        <p className="section-sub">small things I build to scratch my own itches</p>
      </div>
      <ol className="proj-list">
        {PROJECTS.map((p, i) => (
          <li key={i} className="proj-row">
            <a className="proj-link" href={p.url} target="_blank" rel="noopener noreferrer">
              <div className="proj-head">
                <span className="proj-name">{p.name}</span>
                <span className="proj-year">{p.year}</span>
              </div>
              <p className="proj-blurb">{p.blurb}</p>
              <div className="proj-stack">
                {p.stack.map((s, j) => (
                  <span key={j} className="proj-chip">{s}</span>
                ))}
              </div>
            </a>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ---------- post list ----------
const PAGE_SIZE = 5;

function PostList({ posts, onOpen, page, onPage }) {
  const sectionRef = useRef(null);
  const totalPages = Math.max(1, Math.ceil(posts.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const visible = posts.slice(start, start + PAGE_SIZE);

  const handlePage = (p) => {
    onPage(p);
    requestAnimationFrame(() => {
      if (sectionRef.current) {
        const top = sectionRef.current.getBoundingClientRect().top + window.scrollY - 24;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    });
  };

  return (
    <section className="posts" ref={sectionRef}>
      <div className="section-head">
        <h2 className="section-heading">Writing</h2>
        <p className="section-sub">ramblings of a chronic overthinker</p>
      </div>
      <ol className="post-list">
        {visible.map(p => (
          <li key={p.slug} className="post-row">
            <button className="post-link" onClick={() => onOpen(p.slug)}>
              <span className="post-title">{p.title}</span>
              <span className="post-rule" aria-hidden="true"></span>
              <time className="post-date" dateTime={p.date}>{fmtDate(p.date)}</time>
            </button>
          </li>
        ))}
      </ol>
      {totalPages > 1 && (
        <nav className="pager" aria-label="Writing pagination">
          <button
            className="pager-btn"
            onClick={() => handlePage(safePage - 1)}
            disabled={safePage <= 1}
          >
            ← Newer
          </button>
          <span className="pager-meta">Page {safePage} of {totalPages}</span>
          <button
            className="pager-btn pager-right"
            onClick={() => handlePage(safePage + 1)}
            disabled={safePage >= totalPages}
          >
            Older →
          </button>
        </nav>
      )}
    </section>
  );
}

// ---------- footer ----------
function Footer() {
  return (
    <footer className="site-footer">
      <div className="footer-row">
        <a href="https://github.com/sandyjswl" target="_blank" rel="noopener noreferrer">GitHub</a>
        <a href="https://www.linkedin.com/in/sandyjswl/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
        <a href="https://www.instagram.com/sandyjswl" target="_blank" rel="noopener noreferrer">Instagram</a>
        <Link to="/photos">Map</Link>
        <a href="mailto:sandeepjswl123@gmail.com">Email</a>
        <Link to="/resume">Résumé</Link>
      </div>
    </footer>
  );
}

// ---------- reader ----------
function Reader({ post, onBack, newer, older }) {
  const [body, setBody] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setBody(null);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'instant' });

    fetch(`${process.env.PUBLIC_URL}/posts/${post.slug}.md`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then(txt => { if (!cancelled) setBody(txt); })
      .catch(e => { if (!cancelled) setError(e.message); });

    return () => { cancelled = true; };
  }, [post.slug]);

  return (
    <article className="reader">
      <button className="back-link" onClick={onBack}>← Back</button>
      <header className="reader-head">
        <time className="reader-date" dateTime={post.date}>{fmtDate(post.date, { long: true })}</time>
        <h1 className="reader-title">{post.title}</h1>
        <div className="reader-meta">{post.minutes} min read</div>
      </header>
      <div className="reader-body">
        {error ? (
          <p className="reader-error">Couldn't load this post ({error}).</p>
        ) : body === null ? (
          <p className="reader-loading">Loading…</p>
        ) : (
          renderBody(body)
        )}
      </div>

      <nav className="reader-nav">
        {newer ? (
          <button className="rn-btn" onClick={newer.onClick}>
            <span className="rn-dir">← Newer</span>
            <span className="rn-ttl">{newer.title}</span>
          </button>
        ) : <span className="rn-spacer" />}
        {older ? (
          <button className="rn-btn rn-right" onClick={older.onClick}>
            <span className="rn-dir">Older →</span>
            <span className="rn-ttl">{older.title}</span>
          </button>
        ) : <span className="rn-spacer" />}
      </nav>
    </article>
  );
}

// ---------- visitor tracker ----------
async function sendTelegram(msg) {
  const botToken = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
  const chatId = process.env.REACT_APP_TELEGRAM_CHAT_ID;
  if (!botToken || !chatId) return;
  await fetch(`https://api.telegram.org/bot${botToken}/sendMessage`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ chat_id: chatId, text: msg, parse_mode: 'Markdown' }),
  });
}

// Friendly labels for the real content routes — deliberately omits the
// PIN-gated admin routes (/lunch/log, /photos/upload) so visiting your own
// upload tools doesn't page you.
const PAGE_LABELS = {
  '/': 'Writing (home)',
  '/photos': 'Map',
  '/lunch': 'Lunch',
  '/resume': 'Résumé',
};

// Mounted once at the app root (not per-page) so it survives client-side
// route changes and can report on whichever page is visited first.
function VisitorTracker() {
  const location = useLocation();

  useEffect(() => {
    try {
      if (sessionStorage.getItem('visitor_tracked')) return;
      sessionStorage.setItem('visitor_tracked', 'true');
    } catch (e) { /* ignore */ }

    const domain = window.location.hostname;
    const timer = setTimeout(async () => {
      try {
        const ipRes = await fetch('https://ipapi.co/json/');
        if (!ipRes.ok) throw new Error('ipapi failed');
        const loc = await ipRes.json();

        const msg =
`🔔 *New Visitor*
🌍 *Domain:* ${domain}

📍 *Location:*
City: ${loc.city || 'Unknown'}
${loc.region ? `Region: ${loc.region}\n` : ''}Country: ${loc.country_name || 'Unknown'}
${loc.timezone ? `Timezone: ${loc.timezone}\n` : ''}
🌐 *IP:* ${loc.ip || 'Unknown'}

💻 *Device:*
${navigator.userAgent || 'Unknown'}
Screen: ${window.screen.width}x${window.screen.height}
Language: ${navigator.language}

🔗 *Referrer:* ${document.referrer || 'Direct visit'}

⏰ *Time:* ${new Date().toISOString()}`;

        await sendTelegram(msg);
      } catch (e) {
        // silent — tracking is best-effort
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const label = PAGE_LABELS[location.pathname];
    if (!label) return;
    const domain = window.location.hostname;
    const msg =
`📄 *Page visited*
🌍 *Domain:* ${domain}
📍 *Page:* ${label}
⏰ *Time:* ${new Date().toISOString()}`;
    sendTelegram(msg).catch(() => {});
  }, [location.pathname]);

  return null;
}

// ---------- hash routing (within writing page) ----------
function parseHash(hash) {
  const h = (hash || '').replace(/^#\/?/, '');
  if (!h) return { kind: 'home', page: 1 };
  const pageMatch = h.match(/^page\/(\d+)$/);
  if (pageMatch) return { kind: 'home', page: Math.max(1, parseInt(pageMatch[1], 10)) };
  const postMatch = h.match(/^p\/(.+)$/);
  if (postMatch) return { kind: 'reader', slug: decodeURIComponent(postMatch[1]) };
  return { kind: 'home', page: 1 };
}

function stringifyRoute(route) {
  if (route.kind === 'reader') return `#p/${encodeURIComponent(route.slug)}`;
  if (route.kind === 'home' && route.page > 1) return `#page/${route.page}`;
  return '#';
}

// ---------- writing page ----------
function WritingPage({ theme, onToggleTheme }) {
  const [route, setRoute] = useState(() => parseHash(window.location.hash));

  // Warm the map's photo fetch as soon as someone lands on the homepage,
  // so /photos loads instantly if they click through to it later.
  useEffect(() => {
    prefetchMapPhotos();
  }, []);

  useEffect(() => {
    const onHash = () => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const navigate = (next) => {
    const hash = stringifyRoute(next);
    if (window.location.hash !== hash && !(hash === '#' && window.location.hash === '')) {
      window.location.hash = hash;
    } else {
      setRoute(next);
    }
  };

  const [lastHomePage, setLastHomePage] = useState(() => {
    const r = parseHash(window.location.hash);
    return r.kind === 'home' ? r.page : 1;
  });
  useEffect(() => {
    if (route.kind === 'home') setLastHomePage(route.page);
  }, [route]);

  const openPost = (slug) => navigate({ kind: 'reader', slug });
  const goHome = () => navigate({ kind: 'home', page: lastHomePage });
  const setPage = (p) => navigate({ kind: 'home', page: p });

  const view = route.kind === 'reader' ? { kind: 'reader', slug: route.slug } : { kind: 'home' };
  const page = route.kind === 'home' ? route.page : 1;

  const currentPost = view.kind === 'reader' ? POSTS.find(p => p.slug === view.slug) : null;
  const currentPostSlug = currentPost ? currentPost.slug : null;
  useEffect(() => {
    if (!currentPost) return;
    const domain = window.location.hostname;
    const msg =
`📖 *Post opened*
🌍 *Domain:* ${domain}
📝 *Post:* ${currentPost.title}
⏰ *Time:* ${new Date().toISOString()}`;
    sendTelegram(msg).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPostSlug]);

  const currentIdx = currentPost ? POSTS.findIndex(p => p.slug === currentPost.slug) : -1;
  const prev = currentIdx > 0 ? POSTS[currentIdx - 1] : null;
  const next = currentIdx >= 0 && currentIdx < POSTS.length - 1 ? POSTS[currentIdx + 1] : null;

  return (
    <div className="page">
      <Header theme={theme} onToggleTheme={onToggleTheme} />
      <main className="main">
        {view.kind === 'home' ? (
          <div className="home">
            <Bio />
            <PostList posts={POSTS} onOpen={openPost} page={page} onPage={setPage} />
            <Projects />
          </div>
        ) : currentPost ? (
          <Reader
            post={currentPost}
            onBack={goHome}
            newer={prev ? { title: prev.title, onClick: () => openPost(prev.slug) } : null}
            older={next ? { title: next.title, onClick: () => openPost(next.slug) } : null}
          />
        ) : (
          <div className="not-found">
            <p>Post not found.</p>
            <button className="back-link" onClick={goHome}>← Back to home</button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

// ---------- app ----------
function App() {
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('theme') || 'light'; } catch (e) { return 'light'; }
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem('theme', theme); } catch (e) {}
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  return (
    <>
      <Routes>
        <Route path="/" element={<WritingPage theme={theme} onToggleTheme={toggleTheme} />} />
        <Route path="/lunch" element={<FoodPage theme={theme} onToggleTheme={toggleTheme} />} />
        <Route path="/lunch/log" element={<FoodLog />} />
        <Route path="/lunch/log/multi" element={<FoodLogMulti />} />
        <Route path="/cockpit" element={<Cockpit />} />
        <Route path="/resume" element={<ResumePage theme={theme} onToggleTheme={toggleTheme} />} />
        <Route path="/photos" element={<PhotosPage theme={theme} onToggleTheme={toggleTheme} />} />
        <Route path="/photos/upload" element={<MapUpload />} />
      </Routes>
      <VisitorTracker />
    </>
  );
}

export default App;
